import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
} from 'react-native';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { sanitizeResidentFiles } from '@/utils/shared/bulletins/sanitizeResidentFiles';
import { formatDateMMMdd } from '@/utils/residents/date';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents/strings';
import { GlassCard } from '@/components/residents/redesign/GlassCard';
import { NotificationAvatar } from './NotificationAvatar';
import { NotificationItem } from '@/hooks/common/useNotifications';
import { StarRating } from '@/components/residents/marketplace';

const TrashIcon = RESIDENTS_SVG_COMPONENTS.TRASH;
const ChevronDown = RESIDENTS_SVG_COMPONENTS.CHEVRON_DOWN;
const ChevronUp = RESIDENTS_SVG_COMPONENTS.CHEVRON_UP;

interface MobileNotificationCardProps {
  notification: NotificationItem;
  onDelete?: (notification: NotificationItem) => void;
  onExpand?: (notification: NotificationItem) => void;
  onMarketplaceFeedbackPress?: (notification: NotificationItem) => void;
  onCorkBoardWarningPress?: (notification: NotificationItem) => void;
  onPropertyChatPress?: (notification: NotificationItem) => void;
  isUnread?: boolean;
}

export const MobileNotificationCard: React.FC<MobileNotificationCardProps> = ({
  notification,
  onDelete,
  onExpand,
  onMarketplaceFeedbackPress,
  onCorkBoardWarningPress,
  onPropertyChatPress,
  isUnread = false,
}) => {
  const { colors } = useThemeColors();
  const [expanded, setExpanded] = useState(false);

  const isMarketplaceFeedback = notification.notificationType === 'marketplace_feedback';
  const isMarketplaceNotification = notification.notificationType === 'marketplace_notification';
  const isPropertyChat = notification.notificationType === 'property_chat';
  const isCorkBoardWarning = notification.notificationType === 'cork_board_warning';

  const imageFiles = useMemo(
    () => sanitizeResidentFiles(notification.files),
    [notification.files]
  );
  const hasImage = imageFiles.length > 0 || (isMarketplaceFeedback && notification.feedbackRequest?.listing.image);

  const marketplaceFeedbackImage = isMarketplaceFeedback ? notification.feedbackRequest?.listing.image : undefined;

  const receivedFeedbackData = isMarketplaceNotification ? notification.marketplaceNotification?.metadata : undefined;
  const listingImageUrl = receivedFeedbackData?.listingImageUrl || undefined;
  const buyerName = receivedFeedbackData?.buyerName || 'Unknown';
  const feedbackRating = receivedFeedbackData?.rating || 0;
  const reviewText = receivedFeedbackData?.reviewText || '';
  const listingTitle = receivedFeedbackData?.listingTitle || notification.description || '';

  const avatarImageUrl = isMarketplaceNotification
    ? listingImageUrl
    : marketplaceFeedbackImage || undefined;

  const rawBodyText = notification.description || notification.descriptionHtml || notification.body || '';
  const bodyText = rawBodyText.replace(/<[^>]*>/g, '');
  const MAX_CHARS = 90;
  const shouldTruncate = bodyText.length > MAX_CHARS;
  const showExpandLink = !isMarketplaceFeedback && (shouldTruncate || hasImage);

  const authorFirstName = notification.author?.firstName?.trim();
  const authorLastName = notification.author?.lastName?.trim();
  const authorFullName = [authorFirstName, authorLastName].filter(Boolean).join(' ');
  const hasAuthor = Boolean(authorFullName || notification.author?.email);
  const senderName =
    authorFullName ||
    notification.author?.email ||
    notification.title ||
    strings.notifications.mobile.defaultTitle;
  // Show the bulletin subject as a subtitle when we have an author on the top line.
  // If there's no author, the subject is already shown as the sender, so don't repeat it.
  const subjectText = hasAuthor ? notification.title : undefined;

  const displayText = expanded || !shouldTruncate
    ? bodyText
    : bodyText.substring(0, MAX_CHARS) + '...';

  const handleDeletePress = useCallback(() => {
    if (isMarketplaceFeedback) return;
    onDelete?.(notification);
  }, [notification, onDelete, isMarketplaceFeedback]);

  const handleCardPress = useCallback(() => {
    if (isMarketplaceFeedback) {
      onMarketplaceFeedbackPress?.(notification);
      return;
    }
    if (isCorkBoardWarning) {
      onCorkBoardWarningPress?.(notification);
      return;
    }
    if (isPropertyChat) {
      onPropertyChatPress?.(notification);
      return;
    }
  }, [isMarketplaceFeedback, isCorkBoardWarning, isPropertyChat, notification, onMarketplaceFeedbackPress, onCorkBoardWarningPress, onPropertyChatPress]);

  const toggleExpanded = useCallback(() => {
    if (isMarketplaceFeedback) {
      onMarketplaceFeedbackPress?.(notification);
      return;
    }
    if (isCorkBoardWarning) {
      onCorkBoardWarningPress?.(notification);
      return;
    }
    if (isPropertyChat) {
      onPropertyChatPress?.(notification);
      return;
    }

    const newExpanded = !expanded;
    setExpanded(newExpanded);

    if (newExpanded && onExpand) {
      onExpand(notification);
    }
  }, [expanded, notification, onExpand, isMarketplaceFeedback, onMarketplaceFeedbackPress, isCorkBoardWarning, onCorkBoardWarningPress, isPropertyChat, onPropertyChatPress]);

  const isTappableCard = isMarketplaceFeedback || isCorkBoardWarning || isPropertyChat;
  const CardWrapper = isTappableCard ? TouchableOpacity : View;
  const cardWrapperProps = isTappableCard ? {
    onPress: handleCardPress,
    activeOpacity: 0.7,
    accessibilityRole: 'button' as const,
    accessibilityLabel: isCorkBoardWarning
      ? 'View warned thread'
      : isPropertyChat
        ? 'View messages'
        : 'Leave feedback for seller',
  } : {};

  if (isMarketplaceNotification) {
    return (
      <GlassCard borderRadius={16} padding={0}>
        <View style={styles.cardInner}>
          <View style={styles.topRow}>
            <NotificationAvatar
              showUnreadDot={isUnread}
              variant="marketplace"
              imageUrl={avatarImageUrl}
            />
            <View style={styles.receivedFeedbackContent}>
              <View style={styles.headerRow}>
                <Text
                  style={[styles.title, { color: colors.text.primary }]}
                  numberOfLines={1}
                >
                  {buyerName}
                </Text>
                <Text style={[styles.dateText, { color: colors.text.muted }]}>
                  {formatDateMMMdd(notification.createdAt)}
                </Text>
              </View>
              {reviewText ? (
                <Text style={[styles.bodyText, { color: colors.text.secondary }]} numberOfLines={3}>
                  {reviewText}
                </Text>
              ) : (
                <Text style={[styles.bodyText, { color: colors.text.secondary }]} numberOfLines={2}>
                  {listingTitle}
                </Text>
              )}
              <View style={styles.ratingRow}>
                <StarRating rating={feedbackRating} size={16} />
              </View>
            </View>
          </View>
        </View>
      </GlassCard>
    );
  }

  return (
    <GlassCard borderRadius={16} padding={0}>
      <CardWrapper style={styles.cardInner} {...cardWrapperProps}>
        <View style={styles.topRow}>
          <NotificationAvatar
            showUnreadDot={isUnread}
            variant={isPropertyChat ? 'message' : isMarketplaceFeedback ? 'marketplace' : 'property'}
            imageUrl={avatarImageUrl}
          />
          <View style={styles.textContainer}>
            <View style={styles.headerRow}>
              <Text
                style={[styles.title, { color: colors.text.primary }]}
                numberOfLines={1}
              >
                {senderName}
              </Text>
              <View style={styles.dateDeleteRow}>
                <Text style={[styles.dateText, { color: colors.text.muted }]}>
                  {formatDateMMMdd(notification.createdAt)}
                </Text>
                {!isMarketplaceFeedback && !isPropertyChat && (
                  <TouchableOpacity
                    onPress={handleDeletePress}
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    accessibilityRole="button"
                    accessibilityLabel={strings.notifications.mobile.delete.title}
                  >
                    <TrashIcon width={20} height={20} color={colors.text.subtle} />
                  </TouchableOpacity>
                )}
              </View>
            </View>
            {subjectText ? (
              <Text
                style={[styles.subjectText, { color: colors.text.primary }]}
                numberOfLines={2}
              >
                {subjectText}
              </Text>
            ) : null}
            <Text style={[styles.bodyText, { color: colors.text.secondary }]}>
              {displayText}
            </Text>
            {isPropertyChat && (
              <View style={styles.actionLinkContainer}>
                <Text style={styles.actionLinkText}>View messages</Text>
                <ChevronDown width={16} height={16} color={PRIMITIVE_COLORS.resioBlueLight} style={{ transform: [{ rotate: '-90deg' }] }} />
              </View>
            )}
          </View>
        </View>

        {showExpandLink && (
          <TouchableOpacity
            style={styles.showMoreContainer}
            onPress={toggleExpanded}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            accessibilityRole="button"
            accessibilityLabel={expanded ? strings.notifications.mobile.showLess : strings.notifications.mobile.showMore}
          >
            <Text style={styles.showMoreText}>
              {expanded ? strings.notifications.mobile.showLess : strings.notifications.mobile.showMore}
            </Text>
            {expanded ? (
              <ChevronUp width={20} height={20} color={PRIMITIVE_COLORS.resioBlueLight} />
            ) : (
              <ChevronDown width={20} height={20} color={PRIMITIVE_COLORS.resioBlueLight} />
            )}
          </TouchableOpacity>
        )}

        {isMarketplaceFeedback && (
          <View style={styles.actionLinkContainer}>
            <Text style={styles.actionLinkText}>Tap to leave a review</Text>
            <ChevronDown width={16} height={16} color={PRIMITIVE_COLORS.resioBlueLight} style={{ transform: [{ rotate: '-90deg' }] }} />
          </View>
        )}

        {isCorkBoardWarning && (
          <View style={styles.actionLinkContainer}>
            <Text style={styles.actionLinkText}>View thread</Text>
            <ChevronDown width={16} height={16} color={PRIMITIVE_COLORS.resioBlueLight} style={{ transform: [{ rotate: '-90deg' }] }} />
          </View>
        )}

        {!isMarketplaceFeedback && hasImage && expanded && (
          <View style={styles.imagesContainer}>
            {imageFiles.map((file) => (
              <Image
                key={file.id}
                source={{ uri: file.fileUrl }}
                style={styles.image}
                resizeMode="cover"
              />
            ))}
          </View>
        )}

        {isMarketplaceFeedback && marketplaceFeedbackImage && (
          <View style={styles.imageContainer}>
            <Image
              source={{ uri: marketplaceFeedbackImage }}
              style={styles.feedbackImage}
              resizeMode="cover"
            />
          </View>
        )}
      </CardWrapper>
    </GlassCard>
  );
};

const styles = StyleSheet.create({
  cardInner: {
    paddingHorizontal: 20,
    paddingVertical: 16,
    gap: 10,
  },

  topRow: {
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },

  textContainer: {
    flex: 1,
    gap: 4,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
  },
  dateDeleteRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  dateText: {
    ...REDESIGN_TYPOGRAPHY.caption,
    lineHeight: 18,
  },
  title: {
    ...REDESIGN_TYPOGRAPHY.cardTitle,
    lineHeight: 21,
    flex: 1,
  },
  subjectText: {
    ...REDESIGN_TYPOGRAPHY.postTitle,
  },
  bodyText: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
  },

  imagesContainer: {
    gap: 8,
  },
  imageContainer: {},
  image: {
    width: '100%',
    height: 160,
    borderRadius: 12,
  },

  showMoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  showMoreText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
    color: PRIMITIVE_COLORS.resioBlueLight,
  },

  feedbackImage: {
    width: 80,
    height: 80,
    borderRadius: 12,
  },
  actionLinkContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  actionLinkText: {
    ...REDESIGN_TYPOGRAPHY.captionMedium,
    color: PRIMITIVE_COLORS.resioBlueLight,
  },

  receivedFeedbackContent: {
    flex: 1,
    gap: 4,
  },
  ratingRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
});

export default MobileNotificationCard;
